/* =============================================================================
   H-OST · Album-Seite: Kaufen ohne Seitenwechsel und das bewegte Cover.
   Ohne dieses Skript funktioniert alles weiter: Die Kaufknöpfe schicken dann das normale
   Formular ab (/cart/add) und man landet im Warenkorb.
   ============================================================================= */
import { CartLinesUpdateEvent } from '@shopify/events';

const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------------ Kaufen */
function meldung(form, text) {
  const el = form.querySelector('[data-h-meldung]');
  if (el) el.textContent = text;
}

async function warenkorbHolen() {
  const liste = document.querySelector('cart-items-component');
  if (liste) {
    await customElements.whenDefined('cart-items-component');
    if (typeof liste.fetchCartData === 'function') return liste.fetchCartData();
  }
  const antwort = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return antwort.json();
}

async function kaufen(form, knopf) {
  const id = form.querySelector('input[name="id"]')?.value;
  if (!id) return form.submit();

  const text = knopf.querySelector('[data-h-knopf-text]');
  const vorher = text?.textContent;
  knopf.disabled = true;
  knopf.dataset.laed = 'true';
  if (text) text.textContent = 'WIRD HINZUGEFÜGT …';

  const abschnitte = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el.dataset.sectionId)
    .join(',');
  const zugesagt = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: id, quantity: 1 }],
      promise: zugesagt.promise,
    })
  );

  try {
    const antwort = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections: abschnitte }),
      credentials: 'same-origin',
    });
    const daten = await antwort.json();
    const korb = await warenkorbHolen();
    const fehler = Boolean(daten.status);
    zugesagt.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(korb),
      detail: {
        items: korb.items,
        sections: daten.sections,
        didError: fehler,
        source: 'h-ost-album',
        itemCount: 1,
      },
    });
    if (fehler) {
      meldung(form, daten.description || daten.message || 'Das hat nicht geklappt. Bitte noch einmal versuchen.');
    } else {
      meldung(form, 'Im Warenkorb.');
      if (text) text.textContent = 'IM WARENKORB ✓';
      setTimeout(() => {
        if (text) text.textContent = vorher;
        meldung(form, '');
      }, 2600);
    }
  } catch (fehler) {
    zugesagt.reject(fehler);
    // Im Zweifel den normalen Weg nehmen
    form.submit();
    return;
  } finally {
    knopf.disabled = false;
    delete knopf.dataset.laed;
    if (text && text.textContent === 'WIRD HINZUGEFÜGT …') text.textContent = vorher;
  }
}

document.addEventListener('submit', (e) => {
  const form = e.target.closest?.('[data-h-kauf]');
  if (!form) return;
  e.preventDefault();
  const knopf = form.querySelector('button[type="submit"]');
  if (knopf && !knopf.disabled) kaufen(form, knopf);
});

/* ------------------------------------------------------------------ Cover */
/* Ruhelage leicht gedreht wie auf der Band-Seite; am Computer folgt es sanft der Maus,
   am Handy dreht es sich beim Scrollen ein wenig. */
function coverBewegen(cover) {
  const ruhe = { x: 0, y: -9 };
  let ziel = { ...ruhe };
  let jetzt = { ...ruhe };
  let laeuft = false;

  const schritt = () => {
    jetzt.x += (ziel.x - jetzt.x) * 0.08;
    jetzt.y += (ziel.y - jetzt.y) * 0.08;
    cover.style.setProperty('--h-rx', `${jetzt.x.toFixed(2)}deg`);
    cover.style.setProperty('--h-ry', `${jetzt.y.toFixed(2)}deg`);
    if (Math.abs(ziel.x - jetzt.x) > 0.02 || Math.abs(ziel.y - jetzt.y) > 0.02) {
      requestAnimationFrame(schritt);
    } else {
      laeuft = false;
    }
  };
  const anstossen = () => {
    if (!laeuft && !ruhig.matches) {
      laeuft = true;
      requestAnimationFrame(schritt);
    }
  };

  const flaeche = cover.closest('.h-aufmacher') ?? cover;
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    flaeche.addEventListener('pointermove', (e) => {
      const r = cover.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / window.innerWidth;
      const dy = (e.clientY - (r.top + r.height / 2)) / window.innerHeight;
      ziel = { x: Math.max(-7, Math.min(7, -dy * 14)), y: Math.max(-18, Math.min(6, ruhe.y + dx * 18)) };
      anstossen();
    });
    flaeche.addEventListener('pointerleave', () => {
      ziel = { ...ruhe };
      anstossen();
    });
  } else {
    const scrollen = () => {
      const r = cover.getBoundingClientRect();
      const mitte = (r.top + r.height / 2) / window.innerHeight - 0.5;
      ziel = { x: Math.max(-6, Math.min(6, mitte * 10)), y: ruhe.y + Math.max(-5, Math.min(5, mitte * -8)) };
      anstossen();
    };
    window.addEventListener('scroll', scrollen, { passive: true });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', scrollen, { passive: true });
  }
}

document.querySelectorAll('[data-h-cover]').forEach(coverBewegen);
document.documentElement.classList.add('h-bereit');

/* ------------------------------------------------------------------ Widerruf */
/* Beim Absenden Zeitpunkt eintragen und die Angaben für die Bestätigungsseite merken
   (die Seite wird nach dem Absenden neu geladen). */
const widerruf = document.getElementById('Widerruf');
if (widerruf) {
  widerruf.addEventListener('submit', () => {
    const jetzt = new Date();
    const zeit = widerruf.querySelector('[data-h-zeitpunkt]');
    if (zeit) zeit.value = jetzt.toLocaleString('de-DE');
    const daten = {};
    for (const [name, wert] of new FormData(widerruf)) {
      const m = /^contact\[(.+)\]$/.exec(name);
      if (!m || !String(wert).trim() || m[1].startsWith('Zeitpunkt') || m[1] === 'Betreff') continue;
      const schluessel = { email: 'E-Mail', body: 'Artikel' }[m[1]] ?? m[1];
      daten[schluessel] = String(wert);
    }
    try {
      sessionStorage.setItem('h-ost:widerruf', JSON.stringify(daten));
    } catch {
      /* privater Modus: dann ohne Zusammenfassung */
    }
  });
  const erfolg = widerruf.querySelector('[data-h-widerruf-erfolg]');
  if (erfolg) {
    try {
      const daten = JSON.parse(sessionStorage.getItem('h-ost:widerruf') || 'null');
      const liste = erfolg.querySelector('[data-h-widerruf-daten]');
      if (daten && liste) {
        for (const [k, v] of Object.entries(daten)) {
          const dt = document.createElement('dt');
          dt.textContent = k;
          const dd = document.createElement('dd');
          dd.textContent = v;
          liste.append(dt, dd);
        }
        liste.hidden = false;
      }
    } catch {
      /* ohne Zusammenfassung */
    }
    erfolg.focus();
    erfolg.querySelector('[data-h-drucken]')?.addEventListener('click', () => window.print());
  }
}

